package com.devsusana.portfolio.components.sections

import androidx.compose.runtime.Composable
import com.devsusana.portfolio.components.widgets.TechBadge
import com.devsusana.portfolio.data.LocalProjectsData
import com.devsusana.portfolio.i18n.rememberStrings
import com.devsusana.portfolio.styles.SitePalette
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P

/**
 * Detailed showcase section for HomeTutorPro.
 */
@Composable
fun ProjectsSection(modifier: Modifier = Modifier) {
    val project = LocalProjectsData.homeTutorPro
    val colorMode = ColorMode.current
    val strings = rememberStrings()

    Column(
        modifier = modifier
            .fillMaxWidth()
            .id("hometutorpro")
            .styleModifier { property("scroll-margin-top", "5rem") }
            .padding(topBottom = 3.cssRem),
        horizontalAlignment = Alignment.Start,
        verticalArrangement = Arrangement.spacedBy(1.5.cssRem)
    ) {
        // Section Header
        Column(verticalArrangement = Arrangement.spacedBy(0.5.cssRem)) {
            SpanText(
                text = strings.htpEyebrow,
                modifier = Modifier
                    .color(SitePalette.androidGreen)
                    .fontSize(0.9.cssRem)
                    .fontWeight(FontWeight.Bold)
            )

            H2 {
                SpanText(
                    text = "${project.title} — ${strings.htpSubtitle}",
                    modifier = Modifier
                        .color(SitePalette.textPrimary(colorMode))
                        .styleModifier { property("font-size", "clamp(1.4rem, 4.5vw, 2rem)") }
                        .fontWeight(FontWeight.Bold)
                )
            }

            P {
                SpanText(
                    text = strings.htpDescription,
                    modifier = Modifier
                        .color(SitePalette.textSecondary(colorMode))
                        .fontSize(1.1.cssRem)
                )
            }
        }

        // Tags with flexible wrap
        Row(
            horizontalArrangement = Arrangement.spacedBy(0.5.cssRem),
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("flex-wrap", "wrap")
                    property("gap", "0.5rem")
                }
        ) {
            project.tags.forEach { tag ->
                TechBadge(text = tag, highlight = tag.contains("Compose") || tag.contains("Clean") || tag.contains("Agentic"))
            }
        }

        // Main Card / Details Container with fluid padding
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .backgroundColor(SitePalette.surface(colorMode))
                .borderRadius(16.px)
                .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
                .styleModifier {
                    property("padding", "clamp(1rem, 3vw, 2rem)")
                }
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(1.5.cssRem)) {
                // Banner Image
                Image(
                    src = project.bannerPath,
                    alt = "HomeTutorPro Banner",
                    modifier = Modifier
                        .fillMaxWidth()
                        .borderRadius(12.px)
                )

                // Why HomeTutorPro Exists
                Column(verticalArrangement = Arrangement.spacedBy(0.5.cssRem)) {
                    H3 {
                        SpanText(
                            text = strings.htpWhyTitle,
                            modifier = Modifier
                                .color(SitePalette.textPrimary(colorMode))
                                .fontSize(1.35.cssRem)
                                .fontWeight(FontWeight.Bold)
                        )
                    }
                    SpanText(
                        text = strings.htpWhyText,
                        modifier = Modifier
                            .color(SitePalette.textSecondary(colorMode))
                            .lineHeight(1.6.cssRem)
                    )
                }

                // Architecture & Features Summary (responsive grid: stacked on mobile, 2 columns on desktop)
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("display", "grid")
                            property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 320px), 1fr))")
                            property("gap", "2rem")
                        }
                ) {
                    // Architecture Highlights
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(0.5.cssRem)
                    ) {
                        H3 {
                            SpanText(
                                text = strings.htpArchitecturePillarsTitle,
                                modifier = Modifier
                                    .color(SitePalette.textPrimary(colorMode))
                                    .fontSize(1.2.cssRem)
                                    .fontWeight(FontWeight.Bold)
                            )
                        }
                        strings.htpArchitectureHighlights.forEach { highlight ->
                            SpanText(
                                text = "• $highlight",
                                modifier = Modifier
                                    .color(SitePalette.textSecondary(colorMode))
                                    .fontSize(0.95.cssRem)
                                    .lineHeight(1.5.cssRem)
                            )
                        }
                    }

                    // Key Features
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(0.5.cssRem)
                    ) {
                        H3 {
                            SpanText(
                                text = strings.htpKeyFeaturesTitle,
                                modifier = Modifier
                                    .color(SitePalette.textPrimary(colorMode))
                                    .fontSize(1.2.cssRem)
                                    .fontWeight(FontWeight.Bold)
                            )
                        }
                        strings.htpKeyFeatures.forEach { feature ->
                            SpanText(
                                text = "• $feature",
                                modifier = Modifier
                                    .color(SitePalette.textSecondary(colorMode))
                                    .fontSize(0.95.cssRem)
                                    .lineHeight(1.5.cssRem)
                            )
                        }
                    }
                }

                // Screenshots Preview Grid
                Column(verticalArrangement = Arrangement.spacedBy(1.cssRem)) {
                    H3 {
                        SpanText(
                            text = strings.htpScreenshotsTitle,
                            modifier = Modifier
                                .color(SitePalette.textPrimary(colorMode))
                                .fontSize(1.2.cssRem)
                                .fontWeight(FontWeight.Bold)
                        )
                    }
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .styleModifier {
                                property("flex-wrap", "wrap")
                                property("justify-content", "center")
                                property("gap", "1rem")
                            },
                        horizontalArrangement = Arrangement.Center
                    ) {
                        strings.htpScreenshots.forEach { (path, caption) ->
                            val isLandscape = path.contains("landscape") || path.contains("panoramico")
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.spacedBy(0.4.cssRem),
                                modifier = Modifier
                                    .styleModifier {
                                        if (isLandscape) {
                                            property("flex", "0 1 480px")
                                            property("max-width", "520px")
                                            property("min-width", "260px")
                                            property("margin-top", "0.5rem")
                                        } else {
                                            property("flex", "0 1 180px")
                                            property("max-width", "190px")
                                            property("min-width", "130px")
                                        }
                                    }
                            ) {
                                Image(
                                    src = path,
                                    alt = caption,
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .borderRadius(12.px)
                                        .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
                                        .styleModifier {
                                            property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.12)")
                                        }
                                )
                                SpanText(
                                    text = caption,
                                    modifier = Modifier
                                        .color(SitePalette.textSecondary(colorMode))
                                        .fontSize(0.8.cssRem)
                                        .fontWeight(FontWeight.Medium)
                                        .styleModifier {
                                            property("text-align", "center")
                                        }
                                )
                            }
                        }
                    }
                }

                // GitHub CTA Button
                project.githubUrl?.let { url ->
                    Row(
                        modifier = Modifier.padding(top = 1.cssRem),
                        horizontalArrangement = Arrangement.spacedBy(1.cssRem),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Link(
                            path = url,
                            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                            modifier = Modifier
                                .backgroundColor(SitePalette.surface(colorMode))
                                .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.primaryAction(colorMode))
                                .borderRadius(8.px)
                                .padding(leftRight = 1.5.cssRem, topBottom = 0.75.cssRem)
                        ) {
                            SpanText(
                                text = strings.htpCtaGithub,
                                modifier = Modifier
                                    .color(SitePalette.primaryAction(colorMode))
                                    .fontWeight(FontWeight.Bold)
                            )
                        }
                    }
                }
            }
        }
    }
}
